import React, { useState, useRef } from 'react';
import { dayjs, formatLocalTime, toLocalTime } from '../../../components/ex_dayjs';
import { Datagrid, FunctionField, TextField, useDataProvider } from 'react-admin';
import RaList from '../../../components/ra-list/RaList';
import RenderControls from '../../../components/ra-list/RenderControls';
import { convertDataForMSelect, getRaStore, setDefaultValuesFromURL, setRaStore } from '../../../utils/common';
import ActionField from './component/action-field';

/**
 * TrackingFailedOrders Report Component
 *
 * This report displays failed orders by querying SecondaryMarketOrderEx where
 * internal_status = PendingShipment AND primary_order_number IN ('AP-ERROR', 'DIVVY TRANSACTION DUPLICATION')
 * combined with OrderProcessTracking records with Category = "FailedTracking"
 */
const TrackingFailedOrders = () => {
    const dataProvider = useDataProvider();
    const refbtnFilter = useRef(null);
    const [defaultSort] = useState(['utc_Posted_On desc']);
    const [isLoadingCategory, setIsLoadingCategory] = useState(false);
    const [_forRender, setForRender] = useState(null);//trigger re-render row, row must be FunctionField (it has render feature)
    const [allTypes, setAllTypes] = useState(null);
    const [defaultValueFilter, setDefaultValuesFilter] = useState({
        utc_Posted_On: [dayjs().startOf('M'), dayjs()]
    });

    const controls = [
        { component: 'select', name: 'type_eq', label: 'Market', choices: allTypes?.market || [] },
        { component: 'daterange', name: 'utc_Posted_On', label: 'Date', xs: 2, isUTC: true },
        { component: 'text', name: 'event', label: 'Event', xs: 2 },
        { component: 'text', name: 'venue', label: 'Venue', xs: 2 },
        { component: 'number', name: 'Order_Number_eq', label: 'Order Number', xs: 1.5 }
    ];

    /**
     * Handler for row click events
     * Currently logs the record for future implementation
     * @param {number} id - Row ID
     * @param {string} resource - Resource name
     * @param {Object} record - The full record data
     */
    const handleRowClick = (id, resource, record) => {
        // Row click handler for future use
        console.log('Row clicked:', record);
    };

    /**
     * Effect hook to initialize component data on mount
     * Fetches admin permissions, retrieves/caches market and error types,
     * and applies URL query parameters if present
     */
    React.useEffect(() => {
        // Retrieve or fetch market and error types for filters
        // Uses RaStore for caching to avoid repeated API calls
        let _trackingFailedOrderTypes = getRaStore('trackingFailedOrderTypes');
        if (!_trackingFailedOrderTypes) {
            setIsLoadingCategory(true);
            dataProvider.fetchData('TrackingFailedOrders', 'GetAllTypes').then((response: any) => {
                if (response.data) {
                    let errors = Object.keys(response.data.errors).sort();
                    let market = convertDataForMSelect(response.data.market, false);
                    setAllTypes({ market, errors });
                    setRaStore('trackingFailedOrderTypes', { market, errors });

                    setIsLoadingCategory(false);
                }
            }).catch((error: any) => console.log(error));
        } else {
            setAllTypes(_trackingFailedOrderTypes);
        }

        // Apply URL query parameters to default filter values if present
        if (location.search) {
            let newDefaultValue = { ...defaultValueFilter };
            setDefaultValuesFromURL(location.search, newDefaultValue, controls);
            setDefaultValuesFilter(newDefaultValue);
        }
    }, []);

    return (
        <>
            {isLoadingCategory && <p>Loading category ...</p>}
            <RaList
                sortby={defaultSort}
                perPage={25}
                title={'Tracking Failed Orders'}
                filters={
                    <RenderControls
                        controls={controls}
                        refFilter={refbtnFilter}
                        defaultValues={defaultValueFilter}
                        useHashUrl
                        exportFile={{
                            filename: `TrackingFailedOrders-${formatLocalTime(new Date())}`,
                            columns: ['Type', 'Order_Number', 'Event', 'Venue', 'Error', 'Author', 'Utc_Posted_On', 'Action', 'Modified_By', 'Modified'],
                            limit: 1000000
                        }}
                    />
                }
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={(handleRowClick)}
                >
                    <TextField source="type" label={'Market'} />
                    <TextField source="order_Number" label={'Order Num'} />
                    <TextField source="event" label={'Event'} />
                    <TextField source="venue" label={'Venue'} />
                    <TextField source="error" label={'Issue'} />
                    <TextField source="author" label={'Author'} />
                    <FunctionField label='Date' source='utc_Posted_On' render={record => (
                        <><b>{toLocalTime(record.utc_Posted_On).format('MM/DD/YYYY')}</b>
                            <br />{toLocalTime(record.utc_Posted_On).format('hh:mm A')}</>)}
                    />
                    <ActionField label='Action' source='action' setForRender={setForRender}/>
                    <TextField source="modified_By" label={'Modified By'} />
                    <FunctionField label='Modified' source='modified' render={(record) => record.modified && (
                        <><b>{toLocalTime(record.modified).format('MM/DD/YYYY')}</b>
                            <br />{toLocalTime(record.modified).format('hh:mm A')}
                        </>
                    )}
                    />
                </Datagrid>
            </RaList >
        </>
    );
};

const TrackingFailedOrdersPage = {
    name: 'TrackingFailedOrders',
    list: TrackingFailedOrders
};
export default TrackingFailedOrdersPage;
